// src/api/billing.ts
import { apiGet, apiPost } from "./client";

export type Plan = {
  tier: "free" | "pro" | "ultra" | "paid" | "enterprise" | "basic";
  status?: string | null;
  current_period_end?: string | null;
  price_id?: string | null;
};

export function getPlan(): Promise<Plan> {
  return apiGet<Plan>("/billing/plan");
}

export type IntroTrialCheckoutResult = {
  status:
    | "created"
    | "reused"
    | "awaiting_activation"
    | "already_activated"
    | "prior_trial_used"
    | "not_eligible"
    | "invalid_code"
    | "failed";
  url?: string | null;
  trial_months?: number;
  reason?: string;
  retryable?: boolean;
};

export function createIntroTrialCheckout(
  invitationCode?: string,
  signal?: AbortSignal,
): Promise<IntroTrialCheckoutResult> {
  return apiPost<IntroTrialCheckoutResult>(
    "/billing/intro-trial/checkout",
    invitationCode ? { invitation_code: invitationCode } : {},
    signal ? { signal } : undefined,
  );
}

/**
 * Start a Stripe Checkout session.
 * Matches backend's flexible body:
 * { return_to?, success_url?, cancel_url?, price_id? }
 */
export function startCheckout(body: {
  price_id?: string;
  return_to?: string;
  success_url?: string;
  cancel_url?: string;
  // company_id is intentionally ignored; server derives it from the token
  company_id?: string | number;
}): Promise<{ url: string }> {
  // Drop company_id if provided by callers; backend doesn't need it
  const { company_id: _ignore, ...payload } = body ?? {};
  return apiPost<{ url: string }>("/billing/checkout", payload);
}

/** Open the Stripe customer portal (manage subscription). */
export function openPortal(): Promise<{ url: string }> {
  return apiPost<{ url: string }>("/billing/portal", {}); // POST, empty body
}

/**
 * Minimal wrapper used by UpgradeModal.
 * IMPORTANT: send only `return_to` so the server appends ?billing=... flags.
 * (If you pass success_url/cancel_url yourself, you must append those flags manually.)
 */
/** Minimal wrapper used by UpgradeModal. Robust to both server shapes. */
export async function createCheckout(): Promise<{ url: string }> {
  const return_to = window.location.pathname + window.location.search;

  // 1) Old server shape: body is a *string* (return_to)
  try {
    return await apiPost<{ url: string }>("/billing/checkout", return_to);
  } catch (_e) {
    // 2) New server shape: body is an *object* with { return_to }
    return await apiPost<{ url: string }>("/billing/checkout", { return_to });
  }
}


/** Convenience: start checkout and navigate immediately. */
export async function startProCheckout(): Promise<void> {
  const return_to = window.location.pathname + window.location.search;
  const { url } = await startCheckout({ return_to });
  if (url) window.location.assign(url);
}

/** Convenience: open portal and navigate immediately. */
export async function openBillingPortal(): Promise<void> {
  const { url } = await openPortal();
  if (url) window.location.assign(url);
}

export type PaidCheckoutResult = {
  status: "eligible" | "subscribe_again" | "checkout_open" | "pending_confirmation" |
    "manage_subscription" | "pro" | "expired" | "failed";
  attempt_id?: string;
  url?: string;
};
export function createPaidCheckout(requestKey: string, signal?: AbortSignal): Promise<PaidCheckoutResult> {
  return apiPost<PaidCheckoutResult>("/billing/paid-checkout", { request_key: requestKey }, signal ? { signal } : undefined);
}
export function confirmPaidCheckout(attemptId?: string, signal?: AbortSignal): Promise<PaidCheckoutResult> {
  return apiPost<PaidCheckoutResult>("/billing/paid-checkout/status", attemptId ? { attempt_id: attemptId } : {}, signal ? { signal } : undefined);
}
